import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import './DigitalTwinPage.css';

const researchDirections = [
  {
    title: 'MOBILITY',
    image: '/dt-images/DT-mobility.png',
    description: 'Integrating real-time data with digital simulations, the digital twin in mobility enhances urban transportation networks, optimizing traffic flow and public transit efficiency. It provides a dynamic model to test and implement smart city solutions for sustainable and congestion-reduced urban living.',
  },
  {
    title: 'SAFETY',
    image: '/dt-images/DT-safety.png',
    description: 'Digital twin with advanced algorithms analyze patterns from countless sensors across the city, enabling emergency services to respond proactively to incidents before they escalate.',
  },
  {
    title: 'ENERGY',
    image: '/dt-images/DT-energy.png',
    description: 'A digital twin in energy management revolutionizes the grid by simulating energy consumption and distribution, leading to more resilient and efficient power systems. It allows for the seamless integration of renewable energy sources, predicting fluctuations and balancing supply with demand in real-time.',
  },
  {
    title: 'CLIMATE CHANGE',
    image: '/dt-images/DT-climate.png',
    description: 'A digital twin facilitates advanced transportation planning by simulating the effects of extreme weather, enabling cities to optimize routes and schedules for improved safety and reliability. It serves as a crucial tool for adaptive traffic management, ensuring that transportation infrastructure remains resilient in the face of climate-induced challenges.',
  },
  {
    title: 'EQUITY',
    image: '/dt-images/DT-equityy.png',
    description: 'The digital twin in equity ensures fair access to transportation resources, simulating various socio-economic scenarios to identify and bridge gaps in digital inclusion. It aids policymakers in crafting targeted interventions that promote equal opportunities in education, healthcare, and economic participation within the cyber-physical framework.',
  },
  {
    title: 'SUSTAINABILITY',
    image: '/dt-images/DT-sustainability.png',
    description: 'Digital twin technology in transportation systems enables the optimization of traffic flow and public transit, leveraging real-time data and simulations to minimize emissions and energy use. This fosters a sustainable and efficient urban mobility framework, reducing the environmental footprint of city transportation networks.',
  },
];

const projects = [
  {
    title: 'GPU-Accelerated Simulation for Reducing Congestion',
    image: '/dt-images/projects-GPU.gif',
    status: 'Ongoing',
    shortDesc: 'Developing AI-driven simulators for realistic and cost-effective congestion reduction evaluation and strategy testing.',
    longDesc: 'This project addresses a crucial challenge in congestion reduction schemes: the validation of proposals with realistic driver behavior models. Leveraging advancements in artificial intelligence and simulator design, it proposes a two-stage approach to develop an efficient simulator capable of generating complex models of human behavior at intersections through Goal-Conditioned Multi-Agent Reinforcement Learning (GC-MARL) and imitation learning.',
    link: 'https://c2smarter.engineering.nyu.edu/building-intersection-digital-twins-via-gpuaccelerated-human-regularized-reinforcement-learning/',
  },
  {
    title: 'Micro-mobility Testbed',
    image: '/dt-images/crosswalk-covid-gif.gif',
    status: 'Ongoing',
    shortDesc: 'Enhance urban cyclist safety by mapping high-risk areas in NYC, using connected bikes to inform safety improvements.',
    longDesc: 'This project focuses on enhancing cyclist safety in urban settings by identifying and mapping high-risk areas across New York City. Equipping traditional bicycles with portable cameras and ultrasonic sensors transforms them into connected bikes capable of capturing interactions between cyclists and vehicles.',
    link: 'https://c2smarter.engineering.nyu.edu/development-of-mountable-sensors-to-improve-bicyclist-safety/',
  },
  {
    title: 'ATEAM Traffic Incident Testbed',
    image: '/dt-images/projects-mart.png',
    status: 'Ongoing',
    shortDesc: 'Create an AI testbed for traffic incident management, integrating data for predictive analysis and potential digital twin applications.',
    longDesc: 'This project establishes a testbed for managing traffic incidents, leveraging AI-driven approaches for enhanced efficiency. It integrates diverse data sources and types through advanced fusion technologies, enabling the estimation and impact prediction of different traffic incidents on mobility systems.',
    link: 'https://c2smarter.engineering.nyu.edu/developing-a-multi-agency-multi-modal-construction-management-software-tool-to-enhance-coordination-of-construction-projects-city-wide-during-planning-and-operation-phases/',
  },
  {
    title: 'Traffic Digital Twin with FDNY',
    image: '/dt-images/projects-fdny.jpeg',
    status: 'Ongoing',
    shortDesc: 'Partner with FDNY to create a traffic digital twin using SUMO and AI to improve emergency vehicle response times in urban areas.',
    longDesc: 'This project creates a traffic digital twin (TDT) with the FDNY to tackle declining emergency vehicle response times in congested urban settings. Leveraging the Simulation of Urban Mobility (SUMO) and AI, it simulates traffic to evaluate interventions and improve decision-making.',
    link: 'https://c2smarter.engineering.nyu.edu/an-aireinforced-traffic-digital-twin-for-testing-emergency-vehicle-interventions/',
  },
  {
    title: 'Multiscale Urban Traffic Control for Automated Cities',
    image: '/dt-images/projects-congestion.jpg',
    status: 'Ongoing',
    shortDesc: 'This research aims to enhance congestion reduction and safety, measuring performance across mobility, sustainability, and safety metrics.',
    longDesc: 'The UW team has been developing an integrative multiscale modeling and control framework for urban traffic control. The proposed research will be expanded to consider multimodal traffic (especially non-motorized road users) and explore opportunities to test/validate the models using real-world connected and automated vehicle (CAV) testbeds.',
    link: 'https://c2smarter.engineering.nyu.edu/multimodal-multiscale-urban-traffic-control-in-connected-and-automated-cities/',
  },
  {
    title: 'BQE Smart Urban Roadway',
    image: '/dt-images/BQE-spotlight.gif',
    status: 'Completed',
    shortDesc: 'A-WIM testbed enhances transportation accuracy and safety through advanced sensors and digital twin technology.',
    longDesc: 'A cutting-edge A-WIM system testbed in collaboration with local transportation authorities, enhancing the precision of weight measurements across transportation networks. The BQE testbed can emerge as a versatile digital twin platform, advancing interactions with vehicle and road factors.',
    link: 'https://c2smarter.engineering.nyu.edu/development-of-advanced-weigh-in-motion-a-wim-system-for-effective-enforcement-of-overweight-trucks-to-reduce-their-socioeconomic-impact-on-major-highways/',
  },
  {
    title: 'FloodNet Digital Twin',
    image: '/dt-images/floodsense-banner.gif',
    status: 'Completed',
    shortDesc: 'FloodNet NYC implements sensors for enhanced flood management.',
    longDesc: 'This project focuses on implementing the FloodNet monitoring system in NYC to enhance local flood monitoring and risk management. A digital twin is developed for continuous updates on environment factors such as flood risk and impact assessments.',
    link: 'https://c2smarter.engineering.nyu.edu/developing-a-framework-to-optimize-floodnet-sensor-deployments-around-nyc-for-equitable-and-impact-based-hyper-local-street-level-flood-monitoring-and-data-collection/',
  },
  {
    title: 'MATSim Agent-based Simulation',
    image: '/dt-images/MATsim-cyberphysical-testbed.gif',
    status: 'Completed',
    shortDesc: 'MATSim-NYC offers rapid evaluation of transportation technologies and policies through dynamic, multi-agent simulation.',
    longDesc: 'C2SMART developed a multi-agent simulation test bed, dubbed MATSim-NYC, which is intended to be the first of a "Network of Living Labs" virtual test beds for evaluating emerging transportation technologies and policies.',
    link: 'https://c2smarter.engineering.nyu.edu/development-and-tech-transfer-of-multi-agent-virtual-simulation-testbed-ecosystem/',
  },
  {
    title: 'NYC Connected Vehicle Testbed',
    image: '/dt-images/projects-crosswalk.jpeg',
    status: 'Completed',
    shortDesc: 'Testbed for autonomous vehicles enhances safety via V2V/V2I studies and aids visually impaired pedestrians.',
    longDesc: 'The project has created a testbed for autonomous and connected vehicles, focusing on enhancing safety by examining the interaction between human and vehicle factors. A key aspect is supporting visually impaired pedestrians with Personal Information Devices (PID).',
    link: 'https://c2smarter.engineering.nyu.edu/new-york-city-connected-vehicle-pilot/',
  },
  {
    title: 'VR Hardware-in-the-loop Testbed',
    image: '/dt-images/projects-workzone.jpeg',
    status: 'Completed',
    shortDesc: 'Integrate real-time traffic data with VR for urban safety.',
    longDesc: 'This project enhances the VR environment by incorporating hardware-in-the-loop (HIL) to simulate traffic data from real-time HIL streams, setting the stage for exploring digital twin applications.',
    link: 'https://c2smarter.engineering.nyu.edu/work-zone-safety-behavioral-analysis-with-integration-of-vr-and-hardware-in-the-loop/',
  },
  {
    title: 'Digital Campus in Texas',
    image: '/dt-images/projects-texas1.jpg',
    status: 'Completed',
    shortDesc: "Evaluate DT technology's role in integrating transportation with civil infrastructure using UTEP campus as a model.",
    longDesc: 'This research aims to evaluate the efficacy of Digital Twin (DT) technology in understanding the interplay between transportation and other civil infrastructure systems, using the UTEP campus as a living laboratory.',
    link: 'https://c2smarter.engineering.nyu.edu/digital-twin-technologies-towards-understanding-the-interactions-between-transportation-and-other-civil-infrastructure-systems/',
  },
  {
    title: 'Digital Campus in Texas - Phase II',
    image: '/dt-images/projects-texas2.jpeg',
    status: 'Completed',
    shortDesc: 'Continue to enhance UTEP campus infrastructure interaction and integration.',
    longDesc: 'This research aims to assess Digital Twin (DT) technology\'s effectiveness in understanding and enhancing the interaction between transportation and civil infrastructure systems, using the UTEP campus as a case study.',
    link: 'https://c2smarter.engineering.nyu.edu/digital-twin-technologies-towards-understanding-the-interactions-between-transportation-and-other-civil-infrastructure-systems-2/',
  },
  {
    title: 'Connected Automated Transportation Simulation Platform',
    image: '/dt-images/projects-autonomy.jpg',
    status: 'Completed',
    shortDesc: 'Develop a multiscale vehicle-traffic-demand (VTD) simulation platform for connected and automated transportation systems.',
    longDesc: 'The development of the multiscale VTD leverages an existing platform extended to integrate MATSim for large-scale traffic, human activities and travel demand generation processes.',
    link: 'https://c2smarter.engineering.nyu.edu/a-multiscale-simulation-platform-for-connected-and-automated-transportation-systems/',
  },
  {
    title: 'Equitable Tech Transfer Simulator',
    image: '/dt-images/projects-networks.jpg',
    status: 'Completed',
    shortDesc: 'This project develops an API to integrate simulation tools for urban transit, focusing on equitable solutions.',
    longDesc: 'After five years of R&D, this project aims to create a broad API that integrates various independent simulation tools for evaluating algorithms in urban transit systems, focusing on electric transit, freight, and traffic.',
    link: 'https://c2smarter.engineering.nyu.edu/one-to-many-simulator-interface-with-virtual-test-bed-for-equitable-tech-transfer/',
  },
];

const events = [
  {
    title: 'Understand Digital Twins for Transportation Systems Workshop',
    description: 'We held a Digital Twins workshop at the 103rd TRB Annual Meeting, Washington D.C. We invited 10 speakers across academic, industry and agency to present and discuss their work and opinion. A total of 140 people attended the workshop.',
    tags: ['workshop', 'c2smarter', 'nyu'],
    link: 'https://c2smarter.engineering.nyu.edu/digital-twin-workshop-trb-2024/',
    spotlight: true,
  },
  {
    title: 'Simulating the Future of Automated Driving: Unveiling Human Factors Issues and Providing HCI Insights',
    date: 'Thursday, March 14, 2024 | 2:00pm - 3:00pm ET',
    description: 'Dr. Andreas Riener discussed the intersection of human factors and computer interaction in the evaluation of automated driving systems.',
    tags: ['seminar', 'c2smarter', 'nyu'],
    link: 'https://mailchi.mp/e134cd9d9edd/welcome-to-14094245?e=832b179906',
  },
  {
    title: 'Driving Simulators and What You Should Know Before Using Them',
    date: 'Tuesday, March 12, 2024 | 4:00pm - 5:00pm ET',
    description: 'Presented by Chantal Himmels, a Ph.D. candidate with BMW. The talk covered the role of driving simulators in automotive research.',
    tags: ['seminar', 'c2smarter', 'nyu'],
    link: 'https://mailchi.mp/nyu/welcome-to-14094046?e=832b179906',
  },
  {
    title: 'AI for Urban Transportation Digital Twins with Dr. Sharon Di, Columbia University',
    date: 'Friday, February 2, 2024 at 12:00pm - 1:00pm',
    description: 'Dr. Xuan (Sharon) Di discussed enhancing transportation digital twins through AI to process sensor data for traffic optimization.',
    tags: ['seminar', 'cusp', 'nyu'],
    link: 'https://engineering.nyu.edu/events/2024/02/02/research-seminar-series-ai-urban-transportation-digital-twins-dr-sharon-di',
  },
  {
    title: 'Digital Twin Technology: Interactions Between Transportation, Civil Infrastructure Systems: Phase 2',
    date: 'Monday, August 14, 2023 | 12:00pm - 1:00pm ET',
    description: 'Presented by UTEP Professors Dr. Kelvin Cheu and Dr. Ruimin Ke. Explores DT as a tool for the management of civil infrastructure.',
    tags: ['webinar', 'c2smarter', 'utep'],
    link: 'https://mailchi.mp/nyu/welcome-to-13895514',
  },
  {
    title: 'One-to-Many Simulator Interface with Virtual Test Bed for Equitable Tech Transfer',
    date: 'Monday, July 17, 2023 | 12:00pm - 1:00pm ET',
    description: "After five years of R&D, researchers developed a broad API to handle interfacing any simulation with a multi-agent demand simulator.",
    tags: ['webinar', 'c2smarter', 'nyu', 'uw'],
    link: 'https://mailchi.mp/nyu/welcome-to-13893990',
  },
];

const people = [
  { name: 'KAAN OZBAY', title: 'DIRECTOR, C2SMART PROFESSOR, NYU', image: '/dt-images/kaan_ozbay.jpg' },
  { name: 'JOSEPH CHOW', title: 'DEPUTY DIRECTOR, C2SMART, PROFESSOR, NYU', image: '/dt-images/joseph_chow.jpg' },
  { name: 'EUGENE VINITSKY', title: 'ASSISTANT PROFESSOR, NYU', image: '/dt-images/eugene_vinitsky.jpg' },
  { name: 'ZILIN BIAN', title: 'RESEARCHER, NYU', image: '/dt-images/zilin_bian.jpg' },
  { name: 'JINGQIN GAO', title: 'ASSISTANT DIRECTOR, C2SMARTER, NYU', image: '/dt-images/jingqin_gao.jpg' },
  { name: 'FAN ZUO', title: 'RESEARCHER, NYU', image: '/dt-images/fan_zuo.jpg' },
];

function FlipCard({ project }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className="dt-flip-card"
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      onClick={() => window.open(project.link, '_blank')}
    >
      <div className={`dt-flip-card-inner ${flipped ? 'dt-flipped' : ''}`}>
        <div className="dt-flip-card-front">
          <div className={`dt-status-badge ${project.status === 'Ongoing' ? 'dt-ongoing' : 'dt-completed'}`}>
            {project.status}
          </div>
          <div className="dt-project-img" style={{ backgroundImage: `url(${project.image})` }} />
          <div className="dt-project-text">
            <div className="dt-project-title">{project.title}</div>
            <div className="dt-project-description">{project.shortDesc}</div>
          </div>
        </div>
        <div className="dt-flip-card-back">
          {project.longDesc}
        </div>
      </div>
    </div>
  );
}

function ResearchCircle({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="dt-research-circle"
      style={{ backgroundImage: `url(${item.image})` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="dt-research-title-overlay">{item.title}</div>
      {hovered && (
        <div className="dt-research-desc-overlay">
          <p>{item.description}</p>
        </div>
      )}
    </div>
  );
}

function DigitalTwinPage() {
  return (
    <div className="dt-page">
      <Navbar title="Digital Twin Solutions For Mobility and Safety" />

      <div className="dt-nav">
        <div className="dt-nav-logo-wrap" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src="/dt-images/Logo-C2SMARTER.png" alt="C2SMARTER" className="dt-nav-logo-img" />
          <div className="dt-nav-branding">
            <span className="dt-digital-twin">DIGITAL&nbsp;TWIN</span>
            <span className="dt-digital-twin"> | </span>
            <span className="dt-digital-twin-mirror">DIGITAL&nbsp;TWIN</span>
          </div>
        </div>
        <div className="dt-nav-notes">
          Digital Twins @ C2SMARTER Center<br />New York University
        </div>
        <div className="dt-nav-links">
          <a href="#dt-home">HOME</a>
          <a href="#dt-people">PEOPLE</a>
          <a href="#dt-events">NEWS</a>
          <a href="#dt-about">ABOUT</a>
        </div>
      </div>

      <div className="dt-banner">
        <div>
          <span className="dt-banner-dt1">DIGITAL&nbsp;TWIN</span>
          <span className="dt-banner-sep"> | </span>
          <span className="dt-banner-dt2">DIGITAL&nbsp;TWIN</span>
          <div className="dt-banner-subtitle">Research at C2SMARTER Center</div>
        </div>
      </div>

      <div className="dt-hero" id="dt-home" style={{ backgroundImage: "url('/dt-images/digitaltwin_background2.png')" }}>
        <div className="dt-hero-message">Digital Twin solutions for Mobility and Safety</div>
      </div>

      <div className="dt-content">
        <div className="dt-spacer" />
        <h2 className="dt-section-header">Digital Twins Research Directions</h2>
        <p className="dt-section-subheader">
          We focus on five research directions to be better characterized<br />
          by the insights, design, implementation and deployment of digital twin solutions.
        </p>
        <div className="dt-research-grid">
          {researchDirections.map((item, idx) => (
            <ResearchCircle key={idx} item={item} />
          ))}
        </div>

        <div className="dt-spacer" />

        <div className="dt-band" id="dt-events">
          <h2 className="dt-section-header">Events &amp; News</h2>
          <div className="dt-events-grid">
            {events.map((event, idx) => (
              <div key={idx} className={`dt-event-card ${event.spotlight ? 'dt-spotlight' : ''}`}>
                {event.spotlight && <div className="dt-spotlight-badge">SPOTLIGHT</div>}
                <div className="dt-event-body">
                  <h3 className="dt-event-title">{event.title}</h3>
                  {event.date && <div className="dt-event-date">{event.date}</div>}
                  <p className="dt-event-desc">{event.description}</p>
                  <div className="dt-event-footer">
                    <div className="dt-event-tags">
                      {event.tags.map((tag, tidx) => (
                        <span key={tidx} className="dt-tag">{tag}</span>
                      ))}
                    </div>
                    <a href={event.link} target="_blank" rel="noopener noreferrer" className="dt-event-btn">More</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dt-spacer" />

        <div className="dt-band" id="dt-about">
          <h2 className="dt-section-header">Ongoing and Completed Projects</h2>
          <p className="dt-section-subtext">Click each project to visit its website</p>
          <div className="dt-projects-grid">
            {projects.map((project, idx) => (
              <FlipCard key={idx} project={project} />
            ))}
          </div>
        </div>

        <div className="dt-spacer" />

        <div className="dt-band" id="dt-people">
          <h2 className="dt-section-header">People</h2>
          <div className="dt-people-grid">
            {people.map((person, idx) => (
              <div key={idx} className="dt-person-card">
                <div className="dt-person-img-wrap">
                  <div className="dt-person-circle" style={{ backgroundImage: `url(${person.image})` }} />
                </div>
                <div className="dt-person-info">
                  <div className="dt-person-name">{person.name}</div>
                  <div className="dt-person-role">{person.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default DigitalTwinPage;
